import { c as _c } from "react/compiler-runtime";
import React, { useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import MyStreamWaveDesktopTabs from "../MyStreamWaveDesktopTabs";
import { useContentTab } from "@/components/brain/ContentTabContext";
import { useSidebarState } from "../../../../hooks/useSidebarState";
import { ChevronDoubleLeftIcon, ArrowLeftIcon, MagnifyingGlassIcon, ChatBubbleLeftIcon, Squares2X2Icon, ShareIcon, LinkIcon, CheckIcon } from "@heroicons/react/24/outline";
import WavePicture from "../../../waves/WavePicture";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { createBreakpoint } from "react-use";
import WaveDropsSearchModal from "@/components/waves/drops/search/WaveDropsSearchModal";
import { MyStreamWaveTab } from "@/types/waves.types";
import { useWaveChatScrollOptional } from "@/contexts/wave/WaveChatScrollContext";
import type { WaveViewMode } from "@/hooks/useWaveViewMode";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { useWaveShareCopyAction } from "@/hooks/waves/useWaveShareCopyAction";
import WaveDescriptionPopover from "@/components/waves/header/WaveDescriptionPopover";
import { getWaveDescriptionPreviewText } from "@/helpers/waves/waveDescriptionPreview";
import MyStreamActionTooltip from "../MyStreamActionTooltip";
const useBreakpoint = createBreakpoint({
  LG: 1024,
  MD: 768,
  S: 0
});
interface MyStreamWaveTabsDefaultProps {
  readonly wave: ApiWave;
  readonly viewMode: WaveViewMode;
  readonly onToggleViewMode: () => void;
  readonly showGalleryToggle: boolean;
  readonly activeCurationId: string | null;
  readonly onSelectCuration: (curationId: string | null) => void;
}
const MyStreamWaveTabsDefault: React.FC<MyStreamWaveTabsDefaultProps> = t0 => {
  const $ = _c(91);
  const {
    wave,
    viewMode,
    onToggleViewMode,
    showGalleryToggle,
    activeCurationId,
    onSelectCuration
  } = t0;
  const {
    activeContentTab,
    setActiveContentTab
  } = useContentTab();
  const {
    toggleRightSidebar,
    isRightSidebarOpen
  } = useSidebarState();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    isApp
  } = useDeviceInfo();
  const breakpoint = useBreakpoint();
  const showBackButton = breakpoint !== "LG";
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const waveChatScroll = useWaveChatScrollOptional();
  const isDirectMessage = wave.chat.scope.group?.is_direct_message ?? false;
  const showShareAction = !isDirectMessage;
  let t1;
  if ($[0] !== wave) {
    t1 = getWaveDescriptionPreviewText(wave);
    $[0] = wave;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const previewText = t1;
  const showDescriptionPreview = showShareAction && !!previewText;
  let t2;
  if ($[2] !== isDirectMessage || $[3] !== wave.id || $[4] !== wave.name) {
    t2 = {
      waveId: wave.id,
      waveName: wave.name,
      isDirectMessage
    };
    $[2] = isDirectMessage;
    $[3] = wave.id;
    $[4] = wave.name;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    mode: waveLinkActionMode,
    label: waveLinkActionLabel,
    feedbackState: waveLinkActionFeedbackState,
    onClick: handleWaveLinkActionClick
  } = useWaveShareCopyAction(t2);
  let t3;
  if ($[6] !== isApp || $[7] !== router || $[8] !== searchParams || $[9] !== wave.chat.scope.group?.is_direct_message) {
    t3 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      params.delete("serialNo");
      params.delete("divider");
      params.delete("drop");
      params.delete("curation");
      const basePath = getWaveHomeRoute({
        isDirectMessage: wave.chat.scope.group?.is_direct_message ?? false,
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath;
      router.push(newUrl, {
        scroll: false
      });
    };
    $[6] = isApp;
    $[7] = router;
    $[8] = searchParams;
    $[9] = wave.chat.scope.group?.is_direct_message;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleMobileBack = t3;
  let t4;
  if ($[11] !== onSelectCuration || $[12] !== pathname || $[13] !== router || $[14] !== searchParams || $[15] !== setActiveContentTab || $[16] !== wave.id || $[17] !== waveChatScroll) {
    t4 = serialNo => {
      onSelectCuration(null);
      setActiveContentTab(MyStreamWaveTab.CHAT);
      if (waveChatScroll) {
        waveChatScroll.requestScrollToSerialNo({
          waveId: wave.id,
          serialNo
        });
        return;
      }
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.delete("curation");
      params_0.set("serialNo", String(serialNo));
      router.replace(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[11] = onSelectCuration;
    $[12] = pathname;
    $[13] = router;
    $[14] = searchParams;
    $[15] = setActiveContentTab;
    $[16] = wave.id;
    $[17] = waveChatScroll;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const handleSearchSelect = t4;
  const waveLinkActionIconColor = waveLinkActionFeedbackState === "idle" ? "tw-text-iron-200" : "tw-text-emerald-300";
  const headerActionsTooltipId = `my-stream-wave-header-actions-${wave.id}`;
  const galleryToggleLabel = viewMode === "chat" ? "Switch to gallery view" : "Switch to chat view";
  const rightSidebarActionLabel = isRightSidebarOpen ? "Hide right sidebar" : "Show right sidebar";
  let t5;
  if ($[19] !== waveLinkActionFeedbackState || $[20] !== waveLinkActionMode) {
    t5 = () => {
      if (waveLinkActionFeedbackState !== "idle") {
        return <CheckIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
      }
      if (waveLinkActionMode === "share") {
        return <ShareIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
      }
      return <LinkIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
    };
    $[19] = waveLinkActionFeedbackState;
    $[20] = waveLinkActionMode;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const renderWaveLinkActionIcon = t5;
  let t6;
  if ($[22] !== handleMobileBack || $[23] !== showBackButton) {
    t6 = showBackButton && <button onClick={handleMobileBack} className="-tw-ml-2.5 tw-mr-1.5 tw-flex tw-h-full tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-px-2.5 tw-text-iron-300 tw-transition-colors hover:tw-text-iron-50" aria-label="Go back"><ArrowLeftIcon className="tw-h-6 tw-w-6 tw-flex-shrink-0" /></button>;
    $[22] = handleMobileBack;
    $[23] = showBackButton;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const t7 = wave.name;
  const t8 = wave.picture;
  let t9;
  if ($[25] !== wave.contributors_overview) {
    t9 = wave.contributors_overview.map(_temp);
    $[25] = wave.contributors_overview;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t9 || $[28] !== wave.name || $[29] !== wave.picture) {
    t10 = <div className="tw-size-9 tw-flex-shrink-0 tw-rounded-full tw-ring-1 tw-ring-white/30 tw-ring-offset-1 tw-ring-offset-iron-950"><WavePicture name={t7} picture={t8} contributors={t9} /></div>;
    $[27] = t9;
    $[28] = wave.name;
    $[29] = wave.picture;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== previewText || $[32] !== showDescriptionPreview || $[33] !== wave) {
    t11 = showDescriptionPreview ? <WaveDescriptionPopover wave={wave} align="left" ariaLabel="Show wave description" triggerClassName="tw-flex tw-min-w-0 tw-flex-col tw-items-start tw-border-0 tw-bg-transparent tw-p-0 tw-text-left"><h1 className="tw-mb-0 tw-w-full tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95 lg:tw-text-xl">{wave.name}</h1><span className="tw-mt-0.5 tw-block tw-w-full tw-truncate tw-text-xs tw-font-normal tw-text-iron-400">{previewText}</span></WaveDescriptionPopover> : <h1 className="tw-mb-0 tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95 lg:tw-text-xl">{wave.name}</h1>;
    $[31] = previewText;
    $[32] = showDescriptionPreview;
    $[33] = wave;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t6) {
    t12 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-x-3">{t6}{t10}{t11}</div>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t6;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== activeCurationId || $[40] !== galleryToggleLabel || $[41] !== headerActionsTooltipId || $[42] !== onToggleViewMode || $[43] !== showGalleryToggle || $[44] !== viewMode) {
    t13 = showGalleryToggle && !activeCurationId && <button type="button" onClick={onToggleViewMode} aria-label={galleryToggleLabel} data-tooltip-id={headerActionsTooltipId} data-tooltip-content={galleryToggleLabel} className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white">{viewMode === "chat" ? <Squares2X2Icon className="tw-h-4 tw-w-4 tw-flex-shrink-0" /> : <ChatBubbleLeftIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />}</button>;
    $[39] = activeCurationId;
    $[40] = galleryToggleLabel;
    $[41] = headerActionsTooltipId;
    $[42] = onToggleViewMode;
    $[43] = showGalleryToggle;
    $[44] = viewMode;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  let t14;
  if ($[46] !== handleWaveLinkActionClick || $[47] !== headerActionsTooltipId || $[48] !== renderWaveLinkActionIcon || $[49] !== showShareAction || $[50] !== waveLinkActionIconColor || $[51] !== waveLinkActionLabel || $[52] !== waveLinkActionMode) {
    t14 = showShareAction && <button type="button" onClick={handleWaveLinkActionClick} aria-label={waveLinkActionLabel} data-tooltip-id={headerActionsTooltipId} data-tooltip-content={waveLinkActionLabel} data-wave-link-action-mode={waveLinkActionMode} className={`tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white ${waveLinkActionIconColor}`}>{renderWaveLinkActionIcon()}</button>;
    $[46] = handleWaveLinkActionClick;
    $[47] = headerActionsTooltipId;
    $[48] = renderWaveLinkActionIcon;
    $[49] = showShareAction;
    $[50] = waveLinkActionIconColor;
    $[51] = waveLinkActionLabel;
    $[52] = waveLinkActionMode;
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  let t15;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setIsSearchOpen(true);
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  let t16;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <MagnifyingGlassIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  let t17;
  if ($[56] !== headerActionsTooltipId) {
    t17 = <button type="button" onClick={t15} aria-label="Search messages in this wave" data-tooltip-id={headerActionsTooltipId} data-tooltip-content="Search messages in this wave" className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white">{t16}</button>;
    $[56] = headerActionsTooltipId;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  const t18 = `tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-200 tw-transition tw-duration-300 ${isRightSidebarOpen ? "tw-rotate-180 desktop-hover:group-hover:tw-translate-x-0.5" : "tw-rotate-0 desktop-hover:group-hover:-tw-translate-x-0.5"}`;
  let t19;
  if ($[58] !== t18) {
    t19 = <ChevronDoubleLeftIcon strokeWidth={2} className={t18} />;
    $[58] = t18;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  let t20;
  if ($[60] !== headerActionsTooltipId || $[61] !== rightSidebarActionLabel || $[62] !== t19 || $[63] !== toggleRightSidebar) {
    t20 = <button type="button" onClick={toggleRightSidebar} data-tooltip-id={headerActionsTooltipId} data-tooltip-content={rightSidebarActionLabel} className="tw-group tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-800 tw-shadow-[0_12px_28px_rgba(0,0,0,0.35)] tw-backdrop-blur-sm tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-border-iron-500/80 desktop-hover:hover:tw-bg-iron-700/85 desktop-hover:hover:tw-shadow-[0_16px_34px_rgba(0,0,0,0.4)]" aria-label={rightSidebarActionLabel}>{t19}</button>;
    $[60] = headerActionsTooltipId;
    $[61] = rightSidebarActionLabel;
    $[62] = t19;
    $[63] = toggleRightSidebar;
    $[64] = t20;
  } else {
    t20 = $[64];
  }
  let t21;
  if ($[65] !== t13 || $[66] !== t14 || $[67] !== t17 || $[68] !== t20) {
    t21 = <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-x-2 tw-self-stretch">{t13}{t14}{t17}{t20}</div>;
    $[65] = t13;
    $[66] = t14;
    $[67] = t17;
    $[68] = t20;
    $[69] = t21;
  } else {
    t21 = $[69];
  }
  let t22;
  if ($[70] !== t12 || $[71] !== t21) {
    t22 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-4 tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-800 tw-px-2 tw-py-3 sm:tw-px-4">{t12}{t21}</div>;
    $[70] = t12;
    $[71] = t21;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  let t23;
  if ($[73] !== headerActionsTooltipId) {
    t23 = <MyStreamActionTooltip id={headerActionsTooltipId} />;
    $[73] = headerActionsTooltipId;
    $[74] = t23;
  } else {
    t23 = $[74];
  }
  let t24;
  if ($[75] !== activeContentTab || $[76] !== activeCurationId || $[77] !== onSelectCuration || $[78] !== setActiveContentTab || $[79] !== wave) {
    t24 = <div className="tw-border-b tw-border-l-0 tw-border-t-0 tw-border-solid tw-border-iron-800"><MyStreamWaveDesktopTabs activeTab={activeContentTab} wave={wave} setActiveTab={setActiveContentTab} activeCurationId={activeCurationId} onSelectCuration={onSelectCuration} /></div>;
    $[75] = activeContentTab;
    $[76] = activeCurationId;
    $[77] = onSelectCuration;
    $[78] = setActiveContentTab;
    $[79] = wave;
    $[80] = t24;
  } else {
    t24 = $[80];
  }
  let t25;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => setIsSearchOpen(false);
    $[81] = t25;
  } else {
    t25 = $[81];
  }
  let t26;
  if ($[82] !== handleSearchSelect || $[83] !== isSearchOpen || $[84] !== wave) {
    t26 = <WaveDropsSearchModal isOpen={isSearchOpen} onClose={t25} wave={wave} onSelectSerialNo={handleSearchSelect} />;
    $[82] = handleSearchSelect;
    $[83] = isSearchOpen;
    $[84] = wave;
    $[85] = t26;
  } else {
    t26 = $[85];
  }
  let t27;
  if ($[86] !== t22 || $[87] !== t23 || $[88] !== t24 || $[89] !== t26) {
    t27 = <div className="tw-flex tw-w-full tw-flex-col tw-bg-iron-950">{t22}{t23}{t24}{t26}</div>;
    $[86] = t22;
    $[87] = t23;
    $[88] = t24;
    $[89] = t26;
    $[90] = t27;
  } else {
    t27 = $[90];
  }
  return t27;
};
export default MyStreamWaveTabsDefault;
function _temp(c) {
  return {
    pfp: c.contributor_pfp,
    identity: c.contributor_identity
  };
}